/**
 * Zod schema for the `personProfiles` collection (ADR-136).
 *
 * Private contact tier — owner + organizer/admin read. Mirrors the prior
 * `userProfiles` schema but keyed by `personId` instead of `uid`, and
 * includes profile fields for pre-auth persons too. The push tokens and
 * calendar-sync state are only meaningful once the person has claimed
 * their authUid, but the collection holds them uniformly.
 *
 * Doc ID matches the parent `persons` doc ID.
 *
 * See ADR-002 for the three-tier privacy architecture, ADR-022 for the
 * calendar-sync data shape.
 */

import { z } from 'zod';
import type { TimestampLike } from '../types/common';

/**
 * Launch telemetry (ADR-195): which build / OTA update the person's app is
 * running, written by the app on each start. Owner-only write.
 */
export const appClientSchema = z.object({
  version: z.string().nullable(),
  build: z.string().nullable(),
  platform: z.enum(['ios', 'android']),
  runtimeVersion: z.string().nullable(),
  updateId: z.string().nullable(),
  embedded: z.boolean(),
  lastLaunchAt: z.custom<TimestampLike>(),
});

export const pushTokenSchema = z.object({
  token: z.string(),
  platform: z.enum(['ios', 'android']),
  updatedAt: z.custom<TimestampLike>(),
});

/** ADR-022 §6 — snapshot for change detection without a device round-trip. */
export const calendarSyncSessionSnapshotSchema = z.object({
  title: z.string(),
  /** `session.startTime.toDate().getTime()` — UTC ms. */
  startMs: z.number(),
  /** `session.endTime.toDate().getTime()` — UTC ms. */
  endMs: z.number(),
  location: z.string().nullable(),
  description: z.string().nullable(),
});

export const calendarSyncMapEntrySchema = calendarSyncSessionSnapshotSchema.extend({
  /** Opaque ID returned by `expo-calendar.createEventAsync`. */
  nativeEventId: z.string(),
});

export const calendarSyncEventStateSchema = z.object({
  enabled: z.boolean(),
  calendarId: z.string().nullable(),
  sessionMap: z.record(z.string(), calendarSyncMapEntrySchema),
  lastSyncedAt: z.custom<TimestampLike>().nullable(),
});

export const personProfileDocumentSchema = z.object({
  /** Canonical lowercased email. Mirrors persons.email. */
  email: z.string(),

  phone: z.string().nullable(),
  linkedIn: z.string().nullable(),
  website: z.string().nullable(),
  bio: z.string().nullable(),
  title: z.string().nullable(),
  organization: z.string().nullable(),
  /** Professional location supplied by organizer/imports, not a home address. */
  workCity: z.string().nullable().optional(),
  workState: z.string().nullable().optional(),

  /**
   * Multi-device push notification tokens. Empty array for unclaimed
   * persons (they have no device yet); populated as the person registers
   * tokens via the mobile app after claim.
   */
  pushTokens: z.array(pushTokenSchema),

  /** Dietary restrictions for catered events. Article 9 special category. */
  dietaryRestrictions: z.array(z.string()),

  /** Free-text accessibility needs. Article 9 special category. */
  accessibilityNeeds: z.string().nullable(),

  /** Per-event reactive calendar-sync state. ADR-022. */
  calendarSync: z.record(z.string(), calendarSyncEventStateSchema).optional(),

  /** User-overridden default calendar reminder time (minutes). */
  calendarSyncDefaultReminderMinutes: z.number().optional(),

  /** Most recent successful sign-in for this person. Null until claimed. */
  lastSignInAt: z.custom<TimestampLike>().nullable(),

  /** Launch telemetry written by the app on each start (ADR-195). Absent until the first launch on a build that records it. */
  appClient: appClientSchema.optional(),
});
